import { log, logE } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import {
  AdminSeniorVoAddMemberPayload,
  AdminSeniorVoCreateTeamPayload,
  AdminSeniorVoMutationResponse,
  AdminSeniorVoUpdateMemberRolePayload,
  AdminSeniorVoUpdateMemberStatusPayload,
  AdminSeniorVoTeamsResponse,
  normalizeAdminSeniorVoMutationResponse,
  normalizeAdminSeniorVoTeamsResponse,
} from "../models/admin-senior-vo.models";
import axios from "axios";

export const getAdminSeniorVoTeams = async (): Promise<AdminSeniorVoTeamsResponse> => {
  try {
    log("REPO - getAdminSeniorVoTeams");
    const response = await api.get(`${apiUrl}/admin/senior-vo/teams`);
    const teamsResponse = normalizeAdminSeniorVoTeamsResponse(response.data);
    log("REPO - getAdminSeniorVoTeams - response", { status: response.status, count: teamsResponse.teams.length });
    return teamsResponse;
  } catch (error) {
    logE(error);
    throw error;
  }
};

export const addAdminSeniorVoMember = async (
  payload: AdminSeniorVoAddMemberPayload,
): Promise<AdminSeniorVoMutationResponse> => {
  try {
    log("REPO - addAdminSeniorVoMember", { teamId: payload.teamId, teamName: payload.teamName });
    const response = await api.post(`${apiUrl}/admin/senior-vo/teams/${encodeURIComponent(payload.teamId)}/members`, {
      teamId: payload.teamId,
      teamName: payload.teamName,
      memberName: payload.memberName,
      memberEmail: payload.memberEmail,
      memberRole: "VO",
      role: "VO",
      record: {
        TEAM_NAME: payload.teamName,
        MEMBER_NAME: payload.memberName,
        MEMBER_EMAIL: payload.memberEmail,
        MEMBER_ROLE: "VO",
        IS_ACTIVE: true,
      },
    });
    const mutationResponse = normalizeAdminSeniorVoMutationResponse(
      response.data,
      response.status >= 200 && response.status < 300,
    );
    log("REPO - addAdminSeniorVoMember - response", {
      status: response.status,
      success: mutationResponse.success,
    });
    return mutationResponse;
  } catch (error) {
    logE(error);
    throw error;
  }
};

export const addAdminSeniorVoTeam = async (
  payload: AdminSeniorVoCreateTeamPayload,
): Promise<AdminSeniorVoMutationResponse> => {
  try {
    log("REPO - addAdminSeniorVoTeam", { teamName: payload.teamName, seniorVoEmail: payload.seniorVoEmail });
    const response = await api.post(`${apiUrl}/admin/senior-vo/teams`, {
      teamName: payload.teamName,
      seniorVoName: payload.seniorVoName,
      seniorVoEmail: payload.seniorVoEmail,
      memberName: payload.seniorVoName,
      memberEmail: payload.seniorVoEmail,
      memberRole: "Senior VO",
      role: "Senior VO",
      isActive: true,
      record: {
        TEAM_NAME: payload.teamName,
        MEMBER_NAME: payload.seniorVoName,
        MEMBER_EMAIL: payload.seniorVoEmail,
        MEMBER_ROLE: "Senior VO",
        IS_ACTIVE: true,
      },
    });
    const mutationResponse = normalizeAdminSeniorVoMutationResponse(
      response.data,
      response.status >= 200 && response.status < 300,
    );
    if (
      response.status >= 200 &&
      response.status < 300 &&
      (!response.data || typeof response.data !== "object" || !("message" in response.data))
    ) {
      mutationResponse.message = "Senior VO team added successfully.";
    }
    log("REPO - addAdminSeniorVoTeam - response", {
      status: response.status,
      success: mutationResponse.success,
    });
    return mutationResponse;
  } catch (error) {
    logE(error);
    throw error;
  }
};

export const updateAdminSeniorVoMemberRole = async (
  payload: AdminSeniorVoUpdateMemberRolePayload,
): Promise<AdminSeniorVoMutationResponse> => {
  try {
    log("REPO - updateAdminSeniorVoMemberRole", {
      teamId: payload.teamId,
      teamName: payload.teamName,
      memberEmail: payload.memberEmail,
      role: payload.role,
    });
    const response = await api.post(
      `${apiUrl}/admin/senior-vo/teams/${encodeURIComponent(payload.teamId)}/members/${encodeURIComponent(payload.memberEmail)}/role`,
      {
        teamId: payload.teamId,
        teamName: payload.teamName,
        memberName: payload.memberName,
        memberEmail: payload.memberEmail,
        memberRole: payload.role,
        role: payload.role,
        record: {
          TEAM_NAME: payload.teamName,
          MEMBER_NAME: payload.memberName,
          MEMBER_EMAIL: payload.memberEmail,
          MEMBER_ROLE: payload.role,
        },
      },
    );
    const mutationResponse = normalizeAdminSeniorVoMutationResponse(
      response.data,
      response.status >= 200 && response.status < 300,
    );
    if (
      response.status >= 200 &&
      response.status < 300 &&
      (!response.data || typeof response.data !== "object" || !("message" in response.data))
    ) {
      mutationResponse.message = "Member role updated successfully.";
    }
    log("REPO - updateAdminSeniorVoMemberRole - response", {
      status: response.status,
      success: mutationResponse.success,
    });
    return mutationResponse;
  } catch (error) {
    logE(error);
    throw error;
  }
};

export const updateAdminSeniorVoMemberStatus = async (
  payload: AdminSeniorVoUpdateMemberStatusPayload,
): Promise<AdminSeniorVoMutationResponse> => {
  try {
    log("REPO - updateAdminSeniorVoMemberStatus", {
      teamId: payload.teamId,
      teamName: payload.teamName,
      memberEmail: payload.memberEmail,
      isActive: payload.isActive,
    });
    const response = await api.post(
      `${apiUrl}/admin/senior-vo/teams/${encodeURIComponent(payload.teamId)}/members/${encodeURIComponent(payload.memberEmail)}/status`,
      {
        teamId: payload.teamId,
        teamName: payload.teamName,
        memberName: payload.memberName,
        memberEmail: payload.memberEmail,
        isActive: payload.isActive,
        record: {
          TEAM_NAME: payload.teamName,
          MEMBER_NAME: payload.memberName,
          MEMBER_EMAIL: payload.memberEmail,
          IS_ACTIVE: payload.isActive,
        },
      },
    );
    const mutationResponse = normalizeAdminSeniorVoMutationResponse(
      response.data,
      response.status >= 200 && response.status < 300,
    );
    if (
      response.status >= 200 &&
      response.status < 300 &&
      (!response.data || typeof response.data !== "object" || !("message" in response.data))
    ) {
      mutationResponse.message = `Member ${payload.isActive ? "activated" : "marked inactive"} successfully.`;
    }
    log("REPO - updateAdminSeniorVoMemberStatus - response", {
      status: response.status,
      success: mutationResponse.success,
    });
    return mutationResponse;
  } catch (error) {
    logE(error);
    throw error;
  }
};

export const getAdminSeniorVoErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as { error?: string; message?: string } | string | undefined;

    if (typeof responseData === "string") {
      return responseData;
    }

    return responseData?.error ?? responseData?.message ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Senior VO teams request failed.";
};
